import { useEffect } from "react";
import { getProductPhoto, getVialPhoto, getVialKitGallery, isHeroVial } from "@/lib/productPhotos";
import PhotoPlaceholder from "@/components/PhotoPlaceholder";
import FormatPanel from "./FormatPanel";
import VerifiedPanel from "./VerifiedPanel";
import ZoomOverlay from "./ZoomOverlay";
import HoverPreviewThumbs from "./HoverPreviewThumbs";

const PHOTO_TABS = ["SEALED", "OPEN", "PEN"];

// Left column of the compound PDP, image / tabs container. Handles the
// unified media frame (same fixed height for photo and content tabs so
// switching tabs doesn't reflow the layout), the Vial Kit multi-angle
// gallery + thumbnail strip, the tab strip itself, and the COMING SOON
// badge. Zoom-to-lightbox is enabled ONLY when the parent flags the
// product as a Vial Kit, Raw Vials, Pen Kits and Compound Stacks keep
// their current non-zoom treatment.
export default function CompoundProductMedia({
  product,
  tab,
  setTab,
  galleryIdx,
  setGalleryIdx,
  activeTabs,
}) {
  const isPhotoTab = PHOTO_TABS.includes(tab);
  const photoSrc = isPhotoTab ? getProductPhoto(product.slug, tab) : null;
  const vialPhotoSrc =
    product.format === "raw" || product.format === "vial_kit"
      ? getVialPhoto(product.slug)
      : null;
  const isVialKit = product.format === "vial_kit";
  const vialKitGallery = isVialKit ? getVialKitGallery(product.slug) : [];
  const hasGallery = vialKitGallery.length > 1;
  const showingVial = tab === "VIAL" && !!vialPhotoSrc;
  const heroTreatment = showingVial && isHeroVial(product.slug) ? " vial-hero" : "";

  // Unified media container, SAME height for every tab (photo & content
  // alike) so switching tabs doesn't reflow the card. Sized to fit the
  // tallest content (VERIFIED). Desktop uses aspect-square which happens
  // to accommodate every panel comfortably at the current column width;
  // mobile needs a fixed pixel height because aspect-square would collapse
  // to ~356px and clip VERIFIED.
  const mediaContainerBase =
    "relative h-[560px] w-full overflow-hidden rounded-2xl panel-warm sm:h-auto sm:aspect-square";
  const mediaContainerBg = (photoSrc || showingVial)
    ? `bg-white${heroTreatment}`
    : tab === "FORMAT" || tab === "VERIFIED"
    ? "bg-white"
    : "bg-[#F6F7F9]";
  const activeContainerClass = `${mediaContainerBase} ${mediaContainerBg}`;

  // Preload all photo-tab images so tab switches are instant with no fetch.
  const photoUrls = PHOTO_TABS.reduce((acc, t) => {
    const url = getProductPhoto(product.slug, t);
    if (url) acc[t] = url;
    return acc;
  }, {});
  const hasAnyPhoto = Object.keys(photoUrls).length > 0;

  // Zoom is enabled ONLY for Vial Kits (the "closer look at packaging + accessories"
  // use case). Raw Vials and Pen Kits deliberately opt out, batch/expiry copy
  // lives in the description/COA tab there, so a zoom adds no new info.
  const zoomEnabled = isVialKit && tab === "VIAL" && hasGallery;

  const renderMediaContent = () => {
    if (tab === "FORMAT") return <FormatPanel product={product} />;
    if (tab === "VERIFIED") return <VerifiedPanel productSlug={product.slug} />;
    if (tab === "VIAL") {
      if (isVialKit && hasGallery) {
        return vialKitGallery.map((src, i) => (
          <img
            key={src}
            src={src}
            alt={`${product.name}, angle ${i + 1}`}
            className={`absolute inset-0 h-full w-full object-contain object-center transition-opacity duration-200 ease-out ${
              galleryIdx === i ? "opacity-100" : "opacity-0"
            }`}
            data-testid={`vial-kit-gallery-image-${i}`}
            aria-hidden={galleryIdx !== i}
            draggable={false}
          />
        ));
      }
      if (vialPhotoSrc) {
        // Per-slug alt override for compounds through the individual SEO
        // pass, concise, descriptive, no keyword stuffing. Falls back
        // to the templated "<name>, vial" alt for everything else.
        const VIAL_ALT_OVERRIDES = {
          "bpc-157": "BPC-157 research peptide vial",
          "klow": "KLOW research peptide blend vial",
        };
        return (
          <img
            src={vialPhotoSrc}
            alt={VIAL_ALT_OVERRIDES[product.slug] || `${product.name}, vial`}
            className="absolute inset-0 h-full w-full object-contain object-center"
            data-testid="product-photo-vial"
            draggable={false}
          />
        );
      }
      return <PhotoPlaceholder label={`${product.name.toUpperCase()}, VIAL`} uploadable />;
    }
    if (hasAnyPhoto) {
      return PHOTO_TABS.map((t) =>
        photoUrls[t] ? (
          <img
            key={t}
            src={photoUrls[t]}
            alt={`${product.name}, ${t.toLowerCase()}`}
            className={`absolute inset-0 h-full w-full object-contain object-center transition-opacity duration-200 ease-out ${
              tab === t ? "opacity-100" : "opacity-0"
            }`}
            data-testid={`product-photo-${t.toLowerCase()}`}
            aria-hidden={tab !== t}
            draggable={false}
          />
        ) : tab === t ? (
          <div key={t} className="absolute inset-0">
            <PhotoPlaceholder label={`${product.name.toUpperCase()}, ${t}`} uploadable />
          </div>
        ) : null
      );
    }
    return <PhotoPlaceholder label={`${product.name.toUpperCase()}, ${tab}`} uploadable />;
  };

  return (
    <div>
      <div className={activeContainerClass}>
        {renderMediaContent()}
        <ZoomOverlay
          enabled={zoomEnabled}
          images={vialKitGallery}
          index={galleryIdx}
          onIndexChange={setGalleryIdx}
          alt={product.name}
          hintTestId="vial-kit-zoom-hint"
        />
        {product.coming_soon && isPhotoTab && (
          <span
            className="absolute left-4 top-4 z-30 rounded-full bg-amber-500 px-3 py-1.5 font-mono text-[11px] font-bold tracking-[0.12em] text-white"
            data-testid="coming-soon-badge-detail"
          >
            COMING SOON
          </span>
        )}
      </div>

      {tab === "VIAL" && isVialKit && hasGallery && (
        <HoverPreviewThumbs
          images={vialKitGallery}
          activeIdx={galleryIdx}
          onSelect={setGalleryIdx}
          altPrefix={product.name}
          testIdPrefix="vial-kit-gallery"
        />
      )}

      <div className="mt-4 flex gap-2">
        {activeTabs.map((t) => {
          // Vial Kits relabel the VIAL tab to CONTENTS, the gallery now
          // shows the full kit contents (loose vial, needle, BAC water,
          // wipes, sharps bin), not just the vial. Internal state value
          // stays "VIAL" so no other logic branches change; only the
          // rendered label + a mirror data-testid for the new name.
          const label = t === "VIAL" && isVialKit ? "CONTENTS" : t;
          return (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 rounded-md py-2 font-mono text-[11px] tracking-[0.12em] transition-colors ${
                tab === t ? "border border-navy bg-navy text-white" : "btn-outline"
              }`}
              data-testid={`tab-${label.toLowerCase()}`}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
